import { map } from 'lodash/fp';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { pipe } from 'fp-ts/function';
import { Customer } from '@mews-commander/data-access';
import { IconName, Stack } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { TooltippedIcon, OptimusPlainLink } from '@mews-commander/ui';
import { ApplicationState } from '@mews-commander/types';
import { AppClockServiceProps, withAppClockService } from '@mews/framework';
import { mapCustomerClassifications, notEmpty } from '@mews-commander/utils';
import { getCustomer } from '@mews-commander/legacy-entities/src/ducks/customers';
import { hasCustomerValidExternalCreditCard } from '@mews-commander/legacy-entities/src/ducks/paymentCards';
import { CustomerName } from '../../../components/commander-ui';
import { getCustomerDetailUrl } from '../../app/actions/customer';
import ClassificationIcon from '../../../components/commander-ui/ClassificationIcon';

interface OwnProps {
    customerId: string;
    editProfile(customerId: string): void;
    billingScreen(customerId: string): void;
    editInternals(customerId: string): void;
}

interface StateProps {
    customer?: Customer;
    hasValidExternalCreditCard: boolean;
    customerDetailUrl: string;
}

type CompanionListItemNewProps = StateProps & OwnProps;

const CompanionListItemNew = ({
    customer,
    hasValidExternalCreditCard,
    editProfile,
    billingScreen,
    editInternals,
    customerDetailUrl,
}: CompanionListItemNewProps) => {
    if (!customer) {
        return null;
    }
    const { id, classifications: customerClassifications, notes } = customer;
    const classifications = mapCustomerClassifications(customerClassifications);
    const handleEditProfile = () => editProfile(id);
    const handleBillingScreen = () => billingScreen(id);
    const handleEditInternals = () => editInternals(id);

    return (
        <Stack itemAlignment="center" spacing="100" wrap inline>
            <span>
                <TooltippedIcon
                    value={IconName.PaymentReport}
                    tooltip={TextKey.Billing}
                    onClick={handleBillingScreen}
                />
                {hasValidExternalCreditCard ? (
                    <TooltippedIcon value={IconName.PaymentCard} tooltip={TextKey.PaymentCard} />
                ) : null}
            </span>
            <OptimusPlainLink href={customerDetailUrl} onClick={handleEditProfile} inherit>
                <CustomerName id={id} />
            </OptimusPlainLink>
            {notEmpty(classifications)
                ? map(
                      (classification) => (
                          <ClassificationIcon
                              onClick={handleEditInternals}
                              key={classification.value}
                              classification={classification}
                          />
                      ),
                      classifications
                  )
                : null}
            {notes ? <TooltippedIcon value={IconName.Notes} tooltip={notes} onClick={handleEditInternals} /> : null}
        </Stack>
    );
};

type OwnPropsWithAppClockService = OwnProps & AppClockServiceProps;

export default pipe(
    CompanionListItemNew,
    connect<StateProps, null, OwnPropsWithAppClockService, ApplicationState>(
        createStructuredSelector({
            customerDetailUrl: (state: ApplicationState, { customerId }: OwnPropsWithAppClockService) =>
                getCustomerDetailUrl(state, { id: customerId }),
            customer: (state: ApplicationState, { customerId }: OwnPropsWithAppClockService) =>
                getCustomer(state, { id: customerId }),
            hasValidExternalCreditCard: (
                state: ApplicationState,
                { customerId, appClockService }: OwnPropsWithAppClockService
            ) => hasCustomerValidExternalCreditCard(state, { accountId: customerId, appClockService }),
        })
    ),
    withAppClockService
);
